import { Sparkles } from 'lucide-react'
import type { KeyboardEvent, ReactNode } from 'react'
import './CfoInteractive.css'

// Interactivity primitives that make every chart / KPI / value / info box
// "analysis-aware": hover reveals an unobtrusive AI affordance + tooltip, click
// (or Enter/Space) opens the shared analysis modal seeded with that evidence.
// The tooltip is pointer-events:none so it never blocks the underlying click.

type AnalyzableBaseProps = {
  /** Tooltip text, e.g. "Ask AI to explain net income". */
  hint?: string
  /** Opens the analysis modal seeded with this evidence. */
  onAnalyze: () => void
  /** Accessible label for the interactive region. */
  ariaLabel?: string
  disabled?: boolean
  className?: string
  /**
   * When the wrapped child owns its own clicks/keyboard (e.g. an interactive
   * chart whose bars are individually selectable), render a decorative surface
   * — hover ring + spark + tooltip hint — WITHOUT button semantics, so we never
   * nest a button inside a button. The child is responsible for `onAnalyze`.
   */
  interactiveChildren?: boolean
  children: ReactNode
}

function Analyzable({
  hint,
  onAnalyze,
  ariaLabel,
  disabled = false,
  className = '',
  interactiveChildren = false,
  variant,
  children,
}: AnalyzableBaseProps & { variant: 'chart' | 'value' }) {
  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onAnalyze()
    }
  }
  const asButton = !disabled && !interactiveChildren
  return (
    <div
      className={`cfo-analyzable cfo-analyzable-${variant}${disabled ? ' is-disabled' : ''} ${className}`.trim()}
      role={asButton ? 'button' : undefined}
      tabIndex={asButton ? 0 : undefined}
      aria-label={asButton ? ariaLabel || hint : undefined}
      data-hint={hint || undefined}
      onClick={asButton ? onAnalyze : undefined}
      onKeyDown={asButton ? handleKey : undefined}
    >
      {children}
      {!disabled ? (
        <span className="cfo-analyzable-spark" aria-hidden="true">
          <Sparkles size={13} />
        </span>
      ) : null}
    </div>
  )
}

/**
 * Wrap an existing chart (SVG figure) to make it clickable/keyboard-focusable
 * with a hover tooltip. Slice 1 uses it for the net-income TrendLine and the
 * Home net-income bridge.
 */
export function CfoInteractiveChart(props: AnalyzableBaseProps) {
  return <Analyzable {...props} variant="chart" />
}

/**
 * Wrap a KPI value, metric tile, statement cell, or info box. Shows a subtle AI
 * spark on hover; click opens the modal seeded with that value's context.
 */
export function CfoExplainableValue(props: AnalyzableBaseProps) {
  return <Analyzable {...props} variant="value" />
}

export default CfoInteractiveChart
